"use client";

/**
 * ReportChartDisplay — renders a chart produced by the `renderReportChart`
 * tool using the Floats REPORT chart engine (<FloatsChart/> → ECharts), the
 * same renderer the reports product uses. The tool returns a ready
 * `{viz, query, data}` IR triple (data already tenant-scoped + fetched), so
 * this component just renders it.
 */

import * as React from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Plus, X } from "lucide-react";
import { v4 as uuid } from "uuid";

import { FloatsChart } from "@/ui";
import { colors, spacing } from "@/ui/tokens";
import { ToolCard, ToolHeader, ToolBody, ToolProgress, ToolError } from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import type { ChartVizSpec, QuerySpec } from "@/lib/reports/schema";
import type { QueryResult } from "@/lib/reports/adapters/types";
import { ReportBlockExportMenu } from "@/components/reports/ReportBlockExportMenu";
import { reportsApi } from "@/lib/reports/api/client";
import { reportQueryKeys, useReports } from "@/lib/reports/hooks";
import { appendBlockToFirstReportPage, buildSolaChartBlock } from "@/lib/reports/sola-chart-block";
import BarChartIcon from "@mui/icons-material/BarChart";

import styles from "./ReportChartDisplay.module.css";

interface ReportChartOutput {
  success: boolean;
  title?: string;
  businessQuestion?: string;
  chartStrategy?: string;
  explanation?: string;
  rowCount?: number;
  viz?: ChartVizSpec;
  query?: QuerySpec;
  data?: QueryResult;
  error?: string;
}

export function ReportChartDisplay({
  toolCall,
}: ToolDisplayProps<unknown, ReportChartOutput>) {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const toolState = deriveToolState(toolCall);
  const result = toolCall.finalOutput as ReportChartOutput | undefined;
  const chartCaptureRef = React.useRef<HTMLDivElement | null>(null);

  const headerTitle = result?.title || "Chart";
  const hasChart = Boolean(result?.success && result.viz && result.query && result.data);
  const isEmpty = hasChart && (result?.data?.rows?.length ?? 0) === 0;
  const failed = toolState.isError || (toolState.isComplete && result && !result.success);
  const chartId = `sola-chart-${toolCall.toolCallId}`;
  const chartNote = result?.businessQuestion || result?.explanation;

  return (
    <ToolCard status={toolState.status} noPadding>
      <ToolHeader
        title={headerTitle}
        toolName={toolCall.toolName}
        status={toolState.status}
        progress={toolState.progress}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        icon={<BarChartIcon sx={{ fontSize: 18 }} />}
        actions={
          hasChart ? (
            <div className={styles.headerActions}>
              <ReportBlockExportMenu
                blockId={chartId}
                title={headerTitle}
                result={result?.data}
                imageTargetRef={chartCaptureRef}
                shareUrl={() => window.location.href}
                urlLabel="Copy chat URL"
                urlSuccessMessage="Chat URL copied"
              />
              <button
                type="button"
                className={styles.iconButton}
                aria-label="Add chart to dashboard"
                title="Add chart to dashboard"
                data-sola-add-chart-trigger={chartId}
                onClick={() => setAddModalOpen(true)}
              >
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
          ) : undefined
        }
      />
      <ToolBody isOpen={isExpanded}>
        {toolState.isLoading && (
          <ToolProgress value={toolState.progress} variant="dots" label="Generating chart..." />
        )}

        {failed && (
          <ToolError error={result?.error || toolState.error || "Failed to generate chart"} />
        )}

        {toolState.isComplete && hasChart && !isEmpty && (
          <div className={styles.chartStage}>
            {chartNote ? <p className={styles.chartNote}>{chartNote}</p> : null}
            <div ref={chartCaptureRef} className={styles.chartCapture} data-sola-report-chart={chartId}>
              <FloatsChart
                viz={result!.viz!}
                query={result!.query!}
                data={result!.data!}
                height={296}
              />
            </div>
          </div>
        )}

        {toolState.isComplete && hasChart && isEmpty && (
          <div style={{ padding: spacing[4], textAlign: "center", color: colors.text.muted, fontSize: 13 }}>
            No data to chart for this query.
          </div>
        )}
      </ToolBody>
      {addModalOpen && hasChart ? (
        <AddChartToDashboardModal
          title={headerTitle}
          businessQuestion={result?.businessQuestion}
          explanation={result?.explanation}
          query={result!.query!}
          viz={result!.viz!}
          onClose={() => setAddModalOpen(false)}
        />
      ) : null}
    </ToolCard>
  );
}

function AddChartToDashboardModal({
  title,
  businessQuestion,
  explanation,
  query,
  viz,
  onClose,
}: {
  title: string;
  businessQuestion?: string;
  explanation?: string;
  query: QuerySpec;
  viz: ChartVizSpec;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: reports = [], isLoading } = useReports({ include_templates: false }, { enabled: true });
  const dashboards = React.useMemo(() => reports.filter((report) => report.mode === "dashboard"), [reports]);
  const [selectedReportId, setSelectedReportId] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [added, setAdded] = React.useState<{ reportId: string; reportName: string; blockId: string } | null>(null);

  // Adjust-state-during-render: default the dashboard selection to the first
  // available dashboard once the list loads, without a setState-in-effect.
  if (!selectedReportId && dashboards.length > 0) {
    setSelectedReportId(dashboards[0].id);
  }

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  async function addToDashboard() {
    if (!selectedReportId) return;
    setSubmitting(true);
    setError(null);
    try {
      const report = await reportsApi.get(selectedReportId);
      const blockId = uuid();
      const existingCount = report.spec.pages[0]?.blocks.length ?? 0;
      const block = buildSolaChartBlock({
        blockId,
        title,
        businessQuestion,
        explanation,
        query,
        viz,
        existingCount,
      });
      const spec = appendBlockToFirstReportPage(report.spec, block);
      await reportsApi.update(report.id, { spec, author_agent: "sola" });
      await queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
      setAdded({ reportId: report.id, reportName: report.name, blockId });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sola-add-chart-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div>
            <h2 id="sola-add-chart-title" className={styles.modalTitle}>
              {added ? "Chart added" : "Add to dashboard"}
            </h2>
            <p className={styles.modalSubtitle}>
              {added
                ? `${title} was added to ${added.reportName}.`
                : "Choose where this SOLA chart should live as a reusable report section."}
            </p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {added ? (
          <div className={styles.successPanel}>
            <span className={styles.successIcon} aria-hidden="true">
              <Check size={18} />
            </span>
            <Link href={`/reports/${added.reportId}`} className={styles.primaryButton}>
              View on dashboard
            </Link>
          </div>
        ) : (
          <>
            <label className={styles.field}>
              <span className={styles.label}>Dashboard</span>
              <select
                className={styles.select}
                value={selectedReportId}
                onChange={(event) => setSelectedReportId(event.target.value)}
                disabled={isLoading || submitting || dashboards.length === 0}
              >
                {dashboards.length === 0 ? (
                  <option value="">No dashboards available</option>
                ) : (
                  dashboards.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                    </option>
                  ))
                )}
              </select>
            </label>

            {error ? <div className={styles.error}>{error}</div> : null}

            <div className={styles.modalActions}>
              <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={addToDashboard}
                disabled={submitting || isLoading || !selectedReportId}
              >
                {submitting ? "Adding..." : "Add chart"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
